import FamilyForm from 'app/src/features/UserInfo/components/FamilyForm'
import { membershipApi, type UserFamilyMember } from 'app/src/services/welfareportal'
import type { UserFamilyMembersDeleteError } from 'app/src/services/welfareportal/membership-api/membership-components'
import { getFromSessionStorage, queryClient } from 'app/src/shared/helpers'
import { useHttpClient, useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { DataView } from 'primereact/dataview'
import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export function UserFamilyMembersTable(props: { userInfo: membershipApi.MembershipExtended }): ReactElement {
  const [showFamilyForm, setShowFamilyForm] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<UserFamilyMember | undefined>(undefined)
  const httpClient = useHttpClient()
  const { t } = useTranslation('userInfo', { keyPrefix: 'UserInfo.FamilyTable' })
  const relationshipsT = useTranslation('userInfo', { keyPrefix: 'UserInfo.FamilyRelationships' })
  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'

  const deleteFamilyMember = membershipApi.useUserFamilyMembersDelete({
    onSuccess: () => {
      context.showToastMessage({
        content: t('SuccessMessage'),
        severity: 'success',
      })
      membershipApi.invalidateCacheByOperationId(queryClient, 'membership-api.membershipGet')
    },
    onError: (error: UserFamilyMembersDeleteError) => {
      context.showToastMessage({
        content: error.status === 409 ? t('MemberAssociatedErrorMessage') : t('ErrorMessage'),
        severity: 'error',
      })
    },
  })

  const globalTranslation = useTranslation('home', { keyPrefix: 'Global' })

  const context = useWebAppContext()
  const privacyDocumentBodyTemplate = (familyMember: UserFamilyMember, listView: boolean) => {
    if (familyMember.privacyDocument && familyMember.privacyDocument.status === 'uploaded') {
      return (
        <Button
          icon='pi pi-download'
          text
          aria-label={t('AriaLabels.DownloadAction')}
          onClick={async () => {
            const httpResp = await httpClient.downloadFile(
              { input: familyMember.privacyDocument?.urlPath ?? '' },
              familyMember.privacyDocument?.documentName ?? '',
            )
            if (httpResp?.fetchError || httpResp?.blobError || !httpResp?.httpResponse || !httpResp.httpResponse.ok) {
              context.showToastMessage({ content: t('Errors.DownloadPrivacyFileError'), severity: 'error' })
            }
          }}
        />
      )
    } else return listView ? '-' : ''
  }

  const taxDependentBodyTemplate = (familyMember: UserFamilyMember) => {
    return familyMember.taxDependent ? globalTranslation.t('Yes') : globalTranslation.t('No')
  }

  const relationshipBodyTemplate = (familyMember: UserFamilyMember) => {
    const key = familyMember.relationship
    return relationshipsT.t(key)?.shortLabel
  }

  const actionBodyTemplate = (familyMember: UserFamilyMember) => {
    return (
      <>
        <Button
          link
          onClick={() => {
            setSelectedFamilyMember(familyMember)
            setShowFamilyForm(true)
          }}
        >
          {t('UpdateLabel')}
        </Button>
        <Button
          link
          onClick={() => {
            setSelectedFamilyMember(familyMember)
            setShowDeleteModal(true)
          }}
        >
          {t('DeleteLabel')}
        </Button>
      </>
    )
  }

  const itemTemplate = (familyMember: UserFamilyMember, index: number) => {
    const itemClass = index % 2 === 0 ? 'rounded-md bg-[var(--card-background)] m-1 p-4' : 'rounded-md m-1 p-4'
    return (
      <Card className='m-1' key={familyMember.familyMemberId} data-testid='ItemTemplate-Card'>
        <div className={`data-view-item ${itemClass}`}>
          <div className='mb-1'>
            <p className='font-bold'>{t('NameAndSurname')}</p>
            <p>{familyMember.firstName + ' ' + familyMember.lastName}</p>
          </div>
          <div className='mb-1'>
            <p className='font-bold'>{t('FiscalCodeLabel')}</p>
            <p>{familyMember.fiscalCode}</p>
          </div>
          <div className='mb-1'>
            <p className='font-bold'>{t('ParentageLabel')}</p>
            <p>{relationshipBodyTemplate(familyMember)}</p>
          </div>
          <div className='mb-1'>
            <p className='font-bold'>{t('DependentLabel')}</p>
            <p>{taxDependentBodyTemplate(familyMember)}</p>
          </div>
          {context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument && (
            <div className='mb-1'>
              <p className='font-bold'>{t('PrivacyDocumentLabel')}</p>
              <p>{privacyDocumentBodyTemplate(familyMember, true)}</p>
            </div>
          )}
        </div>
        {JSON.parse(campaignStorage)?.active && (
          <div className='flex flex-col flex-wrap content-center items-center pt-2'>
            <div className='flex flex-row flex-wrap content-center items-center gap-2'>
              <Button
                outlined
                rounded
                label={t('UpdateLabel')}
                className='min-w-32 text-[14px] font-normal text-[#323130]'
                onClick={() => {
                  setSelectedFamilyMember(familyMember)
                  setShowFamilyForm(true)
                }}
              />
              <Button
                rounded
                className='min-w-32 text-[14px] font-normal'
                label={t('DeleteLabel')}
                onClick={() => {
                  setSelectedFamilyMember(familyMember)
                  setShowDeleteModal(true)
                }}
              />
            </div>
          </div>
        )}
      </Card>
    )
  }

  const listTemplate = (familyMembers: UserFamilyMember[]) => {
    if (!familyMembers || familyMembers.length === 0) return null

    const list = familyMembers.map((familyMember, index) => {
      return itemTemplate(familyMember, index)
    })

    return <div className='grid-nogutter grid'>{list}</div>
  }

  return props.userInfo?.membershipInfo?.userFamilyMembers ? (
    <>
      <div className='hidden lg:block'>
        <DataTable value={props.userInfo.membershipInfo.userFamilyMembers} stripedRows emptyMessage={t('NoFamilyMembersYet')}>
          <Column field='firstName' header={t('NameLabel')}></Column>
          <Column field='lastName' header={t('SurnameLabel')}></Column>
          <Column field='fiscalCode' header={t('FiscalCodeLabel')}></Column>
          <Column field='relationship' header={t('ParentageLabel')} body={relationshipBodyTemplate}></Column>
          <Column field='taxDependent' header={t('DependentLabel')} body={taxDependentBodyTemplate}></Column>
          {context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument && (
            <Column
              field='privacyDocument'
              header={t('PrivacyDocumentLabel')}
              body={(family) => privacyDocumentBodyTemplate(family, false)}
            ></Column>
          )}
          <Column field='' header='' body={JSON.parse(campaignStorage)?.active ? actionBodyTemplate : undefined}></Column>
        </DataTable>
      </div>
      <div className='block lg:hidden'>
        <DataView
          value={props.userInfo.membershipInfo.userFamilyMembers}
          listTemplate={listTemplate}
          emptyMessage={t('NoFamilyMembersYet')}
        />
      </div>
      {showFamilyForm && (
        <FamilyForm
          membership={props.userInfo}
          familyMember={selectedFamilyMember}
          callbackOnFamilyFormHide={() => setShowFamilyForm(false)}
          isEdit
        />
      )}
      {showDeleteModal && (
        <ConfirmDialog
          visible={showDeleteModal}
          header={t('DeleteFamilyMemberTitle')}
          message={t('DeleteFamilyMemberMessage')}
          accept={() => {
            console.log('Deleting family member with id: ', selectedFamilyMember)
            deleteFamilyMember.mutate({
              pathParams: { familyMembersId: selectedFamilyMember?.familyMemberId ?? '0' },
            })
          }}
          onHide={() => setShowDeleteModal(false)}
          rejectClassName='min-w-32 p-button-outlined p-button-rounded text-[14px] font-normal text-[#323130]'
          acceptClassName='min-w-32 p-button-rounded text-[14px] font-normal'
          pt={{
            root: { className: 'min-w-[50vw]' },
            footer: {
              className: 'flex content-center justify-center',
            },
          }}
          focusOnShow={true}
        />
      )}
    </>
  ) : props.userInfo ? (
    <></>
  ) : (
    <p>{t('NoFamilyMembersYet')}</p>
  )
}
